import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SourcifyCustomerQuoteApprovalLineItemKeys {
  itemNumber = 'itemNumber', // not to be entered by user
  sku = 'sku', // SKU or Item ID
  description = 'description', // verbose description of the item (human parseable)
  Moq = 'Moq', // minimum order quanitity
  FobSalePrice = 'FobSalePrice', // quote price based on FOB freight term
  DdpSeaSalePrice = 'DdpSeaSalePrice', // quote price based on DDP by sea freight term
  DdpAirSalePrice = 'DdpAirSalePrice', // quote price based on DDP air freight term
}

export const sourcifyCustomerQuoteApprovalFileDataSchema = yup.object().shape({
  quoteNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  buyer: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  sample: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  warehouse: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        sku: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        Moq: yup.string().nullable().optional(),
        FobSalePrice: yup.string().nullable().optional(),
        DdpSeaSalePrice: yup.string().nullable().optional(),
        DdpAirSalePrice: yup.string().nullable().optional(),
      }),
    )
    .required(),
  instructions: yup.string().nullable().optional(),
})

export type SourcifyCustomerQuoteApprovalFileData = yup.InferType<
  typeof sourcifyCustomerQuoteApprovalFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<SourcifyCustomerQuoteApprovalFileData>

export const SourcifyCustomerQuoteApprovalFileMappings: RequiredFileData = {
  quoteNumber: 'abd80d3e-1ca1-4c3b-9835-7f12f16f8273',
  dateIssued: 'c3529fc4-ae09-4767-afe5-3bbf4e50743d',
  dateReady: 'af499e18-bc84-4456-ad52-053607809b2c',
  paymentTerms: '57945c4f-f564-4f2e-9b5e-3b203bf34233',
  freightTerms: '5f689971-0391-4849-b822-db3e7a04a083',
  buyer: {
    attn: '59099428-0b18-4f49-ac6a-6e0c8e5ff1c2',
    companyName: '93586236-dccb-445f-9588-6ababef63488',
    addressLine1: 'b2ad6848-381c-46a1-b18c-c845af4b9a07',
    addressLine2: '7e94c29e-3ab4-431a-818c-e1054b111460',
    city: '9a540c20-2272-4211-b65a-072d069b6dc1',
    state: '0a86e928-bd16-471e-a460-f0347da14e36',
    zipCode: 'ed6feb7a-e306-49f5-a810-e07cef7764db',
    country: 'f256dc8b-f055-4ea4-a1a6-fc08245dd3b6',
    phoneNumber: '3516960f-cc2e-487b-b15e-20bb7f41fd13',
    emailAddress: '1cf5c3d9-4c84-4252-8906-6b990ad2eabe',
  },
  sample: {
    attn: '04fa1862-e0fb-4486-8efe-1a90008fe713',
    companyName: '375c7ad8-1bbd-45be-94f0-000c66da5aaf',
    addressLine1: 'e7548c4e-1d1f-4512-8646-b91579384567',
    addressLine2: '4a0dbfd2-4971-4125-af20-65aca26d1a90',
    city: '0b3118ce-6522-4894-8840-a1935f9de6e8',
    state: 'baa4c270-a8a0-4074-bdad-8bcab3ea2012',
    zipCode: '0ad39fde-9485-4860-ae43-6433e2153bce',
    country: '08368253-5e74-491f-868d-9e627f34a6ef',
    phoneNumber: 'f12b4182-098b-48b3-bc6b-f2ed546ef122',
    emailAddress: '17c3a277-f8b9-4f73-8556-58ebb692e314',
  },
  warehouse: {
    attn: '2f7e9aa1-8c45-4f3d-b67e-9d12340abc89',
    companyName: '5d8b2e73-a491-4c6f-95d8-7f3e51b2c4a0',
    addressLine1: '9c4f6d2b-1e8a-4b7d-ae3f-6c2d8f5a1b9e',
    addressLine2: '3a7d9c4e-6b2f-4d8a-91c5-2e8f4b7d3a6c',
    city: '7b4e8d2c-5f9a-4b3d-8e7f-1a2c4d6b9e8f',
    state: '1d4b8e2f-9c6a-4d3b-8a7e-5f2c9d4b8e7a',
    zipCode: '6e2d8f4b-7a3c-4e9d-b5f2-8a4c6e2d9f7b',
    country: '4f8e2d9c-5b7a-4e3d-9f8c-2d4b7e9a6f3d',
    phoneNumber: '8c4e2d7b-9f5a-4e3d-8c7f-4b2e9d8c5f7a',
    emailAddress: '2d7e9f4b-8c5a-4e3d-9f7b-2e8d4c9f7b3a',
  },
  items: [
    {
      itemNumber: null,
      sku: '41248803-4fbd-4762-8b04-66536ff7991c',
      description: '09ecd270-9b4b-4009-927b-891b128a6b2d',
      Moq: '2d192905-4d44-462a-9fbf-0f7a4de137ca',
      FobSalePrice: 'fbf2d7e7-18d1-4304-98eb-29f9ce6a0c0c',
      DdpAirSalePrice: '17b76090-bb91-4afb-aff6-feb48d1c014d',
      DdpSeaSalePrice: 'cf048a22-fdda-412d-99a2-7549f76976b9',
    },
  ],
  instructions: '307dcd9a-20e2-4954-8fc5-f06ee48a2c1c',
}
